import { useEffect, useMemo, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function narrationText(movements) {
  return movements
    .flatMap((m) => [
      m.title || m.heading,
      ...m.blocks.map((b) => (b.t === "gift" ? `${b.name} ${b.text}` : b.text)),
    ])
    .filter(Boolean)
    .join("\n");
}

async function prepareNarration(text) {
  const res = await fetch(`${API}/narration`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("prepare failed");
  let data = await res.json();
  for (let i = 0; data.status === "pending" && i < 120; i++) {
    await new Promise((r) => setTimeout(r, 2500));
    data = await (await fetch(`${API}/narration/${data.key}`)).json();
  }
  if (data.status !== "ready") throw new Error(data.status);
  return data;
}

// idle | preparing | playing | paused | error
export function useNarration(text) {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle");
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [segments, setSegments] = useState([]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const ready = async () => {
    const audio = audioRef.current;
    if (audio.src) return;
    setState("preparing");
    const data = await prepareNarration(text);
    setSegments(data.segments);
    audio.src = `${API}/narration/${data.key}/audio`;
  };

  const toggle = async () => {
    const audio = audioRef.current;
    if (state === "playing") {
      audio.pause();
      setState("paused");
      return;
    }
    try {
      await ready();
      await audio.play();
      setState("playing");
    } catch {
      setState("error");
    }
  };

  const seek = async (seconds) => {
    const audio = audioRef.current;
    try {
      await ready();
      audio.currentTime = seconds;
      setTime(seconds);
      await audio.play();
      setState("playing");
    } catch {
      setState("error");
    }
  };

  const activeIndex = useMemo(() => {
    if (state !== "playing" && state !== "paused") return -1;
    return segments.findIndex((s, i) => time >= s.start && (time < s.end || (segments[i + 1] && time < segments[i + 1].start)));
  }, [segments, time, state]);

  const audioProps = {
    ref: audioRef,
    preload: "none",
    onTimeUpdate: (e) => setTime(e.target.currentTime),
    onLoadedMetadata: (e) => setDuration(e.target.duration),
    onPlaying: () => setState("playing"),
    onEnded: () => {
      setState("idle");
      setTime(0);
    },
    onError: () => setState("error"),
  };

  return { state, time, duration, segments, activeIndex, toggle, seek, audioProps };
}
